// Copyright 2026 Bitech.id. Licensed under the MIT License.

import type { ExceptionValue, Frame, Mechanism } from '../types.js';

const V8_FRAME_PATTERN = /^\s*at\s+(?:(.+?)\s+\()?(?:(.+?):(\d+):(\d+)|(native))\)?\s*$/;

function isNodeModulesPath(filename: string): boolean {
  return filename.includes('node_modules') || filename.startsWith('node:');
}

function parseFrameLine(line: string): Frame | null {
  const match = line.match(V8_FRAME_PATTERN);
  if (!match) return null;

  const [, functionName, file, lineno, colno, native] = match;
  const filename = file ?? native ?? '<anonymous>';

  return {
    filename,
    function: functionName ?? '<anonymous>',
    lineno: lineno ? Number(lineno) : 0,
    colno: colno ? Number(colno) : 0,
    in_app: !isNodeModulesPath(filename),
  };
}

export function parseStackTrace(stack: string | undefined): Frame[] {
  if (!stack) return [];

  const frames = stack
    .split('\n')
    .map(parseFrameLine)
    .filter((frame): frame is Frame => frame !== null);

  return frames.reverse();
}

export function exceptionFromError(error: unknown, mechanism?: Mechanism): ExceptionValue {
  const mech = mechanism ?? { type: 'generic', handled: true };
  if (error instanceof Error) {
    return {
      type: error.name || 'Error',
      value: error.message || String(error),
      stacktrace: { frames: parseStackTrace(error.stack) },
      mechanism: mech,
    };
  }

  return {
    type: 'Error',
    value: typeof error === 'string' ? error : safeStringify(error),
    stacktrace: { frames: [] },
    mechanism: mech,
  };
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}
